import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, ImageIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { getGallery, uploadGalleryImage, deleteGalleryImage, galleryImageUrl } from "../api";
import { toast } from "../hooks/use-toast";

export const GalleryManager = ({ onAuthError, onCount }) => {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null);
  const [title, setTitle] = useState("");
  const inputRef = useRef(null);

  const load = () => getGallery().then((d) => { setItems(d); onCount?.(d.length); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const [i, file] of files.entries()) {
      try {
        setProgress({ n: i + 1, total: files.length, pct: 0 });
        await uploadGalleryImage(file, (pct) => setProgress({ n: i + 1, total: files.length, pct }), title);
      } catch (err) {
        onAuthError(err);
        toast({ title: `Upload failed: ${file.name}`, description: err?.response?.data?.detail, variant: "destructive" });
      }
    }
    setProgress(null);
    setTitle("");
    if (files.length) toast({ title: "Gallery updated" });
    load();
  };

  const remove = async (id) => {
    try {
      await deleteGalleryImage(id);
      toast({ title: "Photo deleted" });
      load();
    } catch (err) {
      onAuthError(err);
    }
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-head font-bold text-xl text-[color:var(--brand-navy)]">Gallery Photos</h2>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Product name (optional, for WhatsApp)" className="max-w-xs ml-auto mr-3" data-testid="gallery-title-input" />
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={onFiles} data-testid="gallery-file-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={!!progress} className="gap-2 brand-btn" data-testid="gallery-upload-btn">
          <Upload size={16} /> {progress ? `Uploading ${progress.n}/${progress.total} (${progress.pct}%)` : "Upload Photos"}
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 py-16 text-center text-slate-400" data-testid="gallery-empty">
          <ImageIcon className="mx-auto mb-2" /> No photos yet. Click "Upload Photos" to add product photos.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} className="relative group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`gallery-admin-item-${it.id}`}>
              <img src={galleryImageUrl(it)} alt="Gallery" className="w-full h-40 object-contain bg-slate-50" />
              {it.title && <div className="px-2 py-1.5 text-xs font-medium truncate">{it.title}</div>}
              <Button size="icon" variant="outline" onClick={() => remove(it.id)} className="absolute top-2 right-2 text-red-600 bg-white" data-testid={`gallery-delete-${it.id}`}>
                <Trash2 size={15} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
